/**
 * AshtamshaSection - Registration for D8 Ashtamsha table with chakra toggle
 * Demonstrates migration from DIVISIONAL_CHARTS with toggle functionality
 */

import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { enhancedRegistry } from '../registry/EnhancedSectionRegistry';
import { AshtamshaInfoTable } from '../components/AshtamshaInfoTable';
import { JatakaResponse } from '../../../../../models/api';

// Import calculator to ensure it's registered
import '../calculators/AshtamshaCalculator';

/**
 * Custom loading component for D8 Ashtamsha
 */
const AshtamshaLoadingSpinner: React.FC<{ progress?: number }> = ({ progress = 0 }) => {
    return (
        <div style={{
            padding: '20px',
            textAlign: 'center',
            background: '#fff3cd',
            borderRadius: '8px',
            border: '1px solid #ffeaa7'
        }}>
            <div style={{
                fontSize: '14px',
                color: '#856404',
                marginBottom: '12px'
            }}>
                🎯 Calculating D8 Ashtamsha...
            </div>
            {progress > 0 && (
                <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: '#ffeaa7',
                    borderRadius: '2px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                }}>
                    <div style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: '#f39c12',
                        transition: 'width 0.3s ease'
                    }} />
                </div>
            )}
            <div style={{
                fontSize: '12px',
                color: '#6c757d'
            }}>
                {progress > 0 ? `${Math.round(progress)}% complete` : 'Analyzing 8-fold divisions...'}
            </div>
        </div>
    );
};

// Register the enhanced section with chakra toggle
enhancedRegistry.register({
    id: 'ashtamsha-d8',
    parentSection: 'Divisional',
    label: 'D8 Ashtamsha Vara',
    component: AshtamshaInfoTable,
    type: 'table',
    order: 20, // After D7

    // Lazy loading with D8 calculation
    lazy: {
        calculator: 'ashtamsha-d8',
        loader: AshtamshaLoadingSpinner,
        dependencies: [
            'graha_positions.planets',
            'detailed_panchanga.hora_details'
        ]
    },

    // Chakra integration - show chakra on table expand
    chakra: {
        id: 'ashtamsha-chakra',
        triggerOn: 'expand',
        autoRegister: true
    },

    // State persistence with toggle state
    persistence: {
        enabled: true,
        stateKeys: ['expanded', 'chakraEnabled', 'colorScheme'],
        namespace: 'ashtamsha-d8'
    },

    // Event handlers for D8 + Chakra monitoring
    events: {
        onMount: (apiResponse: JatakaResponse) => {
            devLog(`🎯 AshtamshaSection: Mounted D8 with ${apiResponse.graha_positions?.planets?.length || 0} planets`);
        },

        onExpand: (isExpanded: boolean, apiResponse?: JatakaResponse) => {
            devLog(`📊 AshtamshaSection: D8 ${isExpanded ? 'Expanded' : 'Collapsed'}`);

            if (isExpanded) {
                devLog(`   Chart has ${apiResponse?.graha_positions?.planets?.length || 0} planets for 8-fold division`);
            }
        },

        onCalculationStart: (calculatorId: string) => {
            devLog(`⚡ AshtamshaSection: Starting D8 calculation with "${calculatorId}"`);
        },

        onCalculationComplete: (result: any, calculatorId: string) => {
            devLog(`✅ AshtamshaSection: D8 calculation complete!`);
            devLog(`   Calculator: ${calculatorId}`);
            devLog(`   Total planets: ${result.totalPlanets}`);
            devLog(`   Divisions: ${result.divisionsCount}`);
            devLog(`   Calculation time: ${result.calculationTime}ms`);

            // Log division distribution
            const occupiedDivisions = Object.values(result.planetsByDivision).filter((planets: any) => planets.length > 0).length;
            devLog(`   Occupied divisions: ${occupiedDivisions}/${result.divisionsCount}`);
        },

        onCalculationError: (error: Error, calculatorId: string) => {
            devError(`❌ AshtamshaSection: D8 calculation error in "${calculatorId}":`, error);
        },

        onChakraToggle: (enabled: boolean, chakraId: string) => {
            devLog(`🎯 AshtamshaSection: Ashtamsha Chakra "${chakraId}" ${enabled ? 'enabled' : 'disabled'}`);
        }
    },

    // Only show if we have planet data
    condition: (apiResponse: JatakaResponse) => {
        const hasPlants = apiResponse.graha_positions?.planets && apiResponse.graha_positions.planets.length > 0;

        if (!hasPlants) {
            devWarn(`⚠️ AshtamshaSection: No planet data for D8 calculation, hiding section`);
        }

        return !!hasPlants;
    },

    // Component props for toggle and colors
    componentProps: {
        showChakraToggle: true,
        enableColorScheme: true,
        showEmptyDivisions: true
    }
});

// D8 toggle is now handled by UniversalDivisionalToggle in PanchangaSection
// with itemId='d8-ashtamsha' - no separate registration needed

// Export for backward compatibility
export { AshtamshaInfoTable };
export default AshtamshaInfoTable;